import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PHOTOGRAPHY_ITEMS,
  PHOTOGRAPHY_PRESET_PACKAGES,
  getAllItemCategories,
} from '../data/photographyItems';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, X, Plus, Minus, CaretLeft, Heart, Gift, Baby, House, Briefcase, UserCircle, Users, HandsPraying, Star } from '@phosphor-icons/react';

const TIER_STYLES = {
  budget: { border: 'border-green-500', bg: 'bg-green-50', badge: 'bg-green-600' },
  luxury: { border: 'border-amber-500', bg: 'bg-amber-50', badge: 'bg-amber-600' },
  premium: { border: 'border-purple-500', bg: 'bg-purple-50', badge: 'bg-purple-600' },
  ultra_luxury: { border: 'border-rose-500', bg: 'bg-rose-50', badge: 'bg-rose-600' },
};

// Event types (same values as InstantPricingPage EVENT_TYPES)
const EVENT_TYPES = [
  { value: 'marriage', label: 'Marriage', icon: Heart },
  { value: 'birthday', label: 'Birthday', icon: Gift },
  { value: 'engagement', label: 'Engagement / Reception', icon: Heart },
  { value: 'baby_shower', label: 'Baby Shower / Seemantham', icon: Baby },
  { value: 'house_warming', label: 'House Warming', icon: House },
  { value: 'corporate', label: 'Corporate Event', icon: Briefcase },
  { value: 'anniversary', label: 'Anniversary', icon: Heart },
  { value: 'puberty_ceremony', label: 'Puberty Ceremony', icon: UserCircle },
  { value: 'retirement', label: 'Retirement Party', icon: Users },
  { value: 'religious', label: 'Religious / Pooja', icon: HandsPraying },
  { value: 'other', label: 'Other Events', icon: Star },
];

const MARRIAGE_SUBTYPES = [
  { value: 'hindu', label: 'Hindu Wedding' },
  { value: 'muslim', label: 'Muslim Wedding / Nikkah' },
  { value: 'christian', label: 'Christian Wedding' },
  { value: 'brahmin', label: 'Brahmin Wedding' },
];

const ENGAGEMENT_SUBTYPES = [
  { value: 'hindu', label: 'Hindu' },
  { value: 'muslim', label: 'Muslim' },
  { value: 'christian', label: 'Christian' },
];

const needsSubtype = (eventType) => eventType === 'marriage' || eventType === 'engagement';

const getSubtypeOptions = (eventType) => {
  if (eventType === 'marriage') return MARRIAGE_SUBTYPES;
  if (eventType === 'engagement') return ENGAGEMENT_SUBTYPES;
  return [];
};

const packageKeyFor = (eventType, subtype, tier) =>
  `${eventType}__${subtype || 'none'}__${tier}`;

const PhotographerSelector = ({ onPackageAdd, existingPackageKeys = [] }) => {
  // Flow state
  const [step, setStep] = useState('event'); // 'event' | 'subtype' | 'tier' | 'customize'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [packageItems, setPackageItems] = useState([]);
  const [packagePrice, setPackagePrice] = useState('');
  // Work images for THIS package — shown in a swipeable lightbox on the
  // customer side when they tap this photographer.
  const [workImages, setWorkImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleWorkImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const next = [...workImages];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        if (data?.url) {
          next.push(data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Image upload failed');
      }
    }
    setWorkImages(next);
    setUploading(false);
    e.target.value = '';
  };

  const resetToEvent = () => {
    setStep('event');
    setSelectedEvent(null);
    setSelectedSubtype(null);
    setEditingTier(null);
    setPackageItems([]);
    setPackagePrice('');
    setWorkImages([]);
  };

  const handleEventPick = (ev) => {
    setSelectedEvent(ev);
    if (needsSubtype(ev.value)) {
      setStep('subtype');
    } else {
      setSelectedSubtype(null);
      setStep('tier');
    }
  };

  const handleSubtypePick = (st) => {
    setSelectedSubtype(st);
    setStep('tier');
  };

  const startCustomize = (preset) => {
    const items = PHOTOGRAPHY_ITEMS.map((item) => {
      const presetItem = preset.defaultItems.find((d) => d.name === item.name);
      return {
        name: item.name,
        category: item.category,
        defaultPrice: item.defaultPrice,
        qty: presetItem ? presetItem.qty : 0,
        active: !!presetItem,
      };
    });
    setPackageItems(items);
    setPackagePrice(String(preset.price));
    setEditingTier(preset);
    setStep('customize');
  };

  const toggleItem = (index) => {
    const updated = [...packageItems];
    updated[index].active = !updated[index].active;
    if (updated[index].active && updated[index].qty === 0) updated[index].qty = 1;
    if (!updated[index].active) updated[index].qty = 0;
    setPackageItems(updated);
  };

  const changeQty = (index, delta) => {
    const updated = [...packageItems];
    const newQty = Math.max(0, updated[index].qty + delta);
    updated[index].qty = newQty;
    updated[index].active = newQty > 0;
    setPackageItems(updated);
  };

  const handleSave = () => {
    const activeItems = packageItems.filter((i) => i.active && i.qty > 0);
    if (activeItems.length === 0) return;

    const pkg = {
      event_type: selectedEvent.value,
      event_type_label: selectedEvent.label,
      event_subtype: selectedSubtype ? selectedSubtype.value : null,
      event_subtype_label: selectedSubtype ? selectedSubtype.label : null,
      tier: editingTier.tier,
      name: editingTier.name,
      description: editingTier.description,
      price: parseInt(packagePrice, 10) || editingTier.price,
      items: activeItems.map((i) => ({ name: i.name, category: i.category, qty: i.qty })),
      work_images: [...workImages],
    };
    onPackageAdd(pkg);
    resetToEvent();
  };

  const Breadcrumb = () => (
    <div className="flex items-center gap-2 text-sm text-text-secondary mb-4 flex-wrap" data-testid="photo-breadcrumb">
      <button type="button" onClick={resetToEvent} className="hover:text-brand-primary font-medium">Event</button>
      {selectedEvent && (
        <>
          <span>›</span>
          <button
            type="button"
            onClick={() => { if (needsSubtype(selectedEvent.value)) setStep('subtype'); else setStep('tier'); setEditingTier(null); }}
            className="hover:text-brand-primary font-medium"
          >
            {selectedEvent.label}
          </button>
        </>
      )}
      {selectedSubtype && (
        <>
          <span>›</span>
          <button type="button" onClick={() => { setStep('tier'); setEditingTier(null); }} className="hover:text-brand-primary font-medium">
            {selectedSubtype.label}
          </button>
        </>
      )}
      {step === 'customize' && editingTier && (
        <>
          <span>›</span>
          <span className="text-text-primary font-medium">{editingTier.name}</span>
        </>
      )}
    </div>
  );

  // Step 1: Event Type picker
  if (step === 'event') {
    return (
      <div className="space-y-4">
        <div className="text-sm text-text-secondary mb-2">Step 1 — Select the event type this package is for.</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photo-event-grid">
          {EVENT_TYPES.map((ev) => {
            const Icon = ev.icon;
            return (
              <button
                key={ev.value}
                type="button"
                onClick={() => handleEventPick(ev)}
                className="p-4 border border-[#E8E1D5] bg-white hover:border-brand-primary hover:bg-brand-primary/5 transition-all text-left"
                data-testid={`photo-event-${ev.value}`}
              >
                <Icon size={22} weight="duotone" className="text-brand-primary mb-2" />
                <div className="text-sm font-medium text-text-primary leading-tight">{ev.label}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Step 2: Sub-type picker (only for marriage & engagement)
  if (step === 'subtype' && selectedEvent) {
    const subtypes = getSubtypeOptions(selectedEvent.value);
    return (
      <div className="space-y-4">
        <Breadcrumb />
        <div className="text-sm text-text-secondary mb-2">Step 2 — Select the {selectedEvent.label.toLowerCase()} sub-type.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="photo-subtype-grid">
          {subtypes.map((st) => (
            <button
              key={st.value}
              type="button"
              onClick={() => handleSubtypePick(st)}
              className="p-4 border border-[#E8E1D5] bg-white hover:border-brand-primary hover:bg-brand-primary/5 transition-all text-left"
              data-testid={`photo-subtype-${st.value}`}
            >
              <div className="text-base font-medium text-text-primary">{st.label}</div>
            </button>
          ))}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={resetToEvent} className="text-text-secondary">
          <CaretLeft size={16} className="mr-1" /> Change event type
        </Button>
      </div>
    );
  }

  // Step 3: Tier picker
  if (step === 'tier' && selectedEvent) {
    return (
      <div className="space-y-4">
        <Breadcrumb />
        <div className="text-sm text-text-secondary mb-2">Step {needsSubtype(selectedEvent.value) ? '3' : '2'} — Choose a package tier to customize.</div>
        {PHOTOGRAPHY_PRESET_PACKAGES.map((preset) => {
          const style = TIER_STYLES[preset.tier];
          const key = packageKeyFor(selectedEvent.value, selectedSubtype?.value, preset.tier);
          const alreadyAdded = existingPackageKeys.includes(key);
          return (
            <Card
              key={preset.tier}
              className={`${style.border} border-2 ${alreadyAdded ? 'opacity-50' : style.bg} transition-all`}
              data-testid={`tier-card-${preset.tier}`}
            >
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${style.badge}`}>
                        {preset.name}
                      </span>
                      {alreadyAdded && (
                        <span className="text-xs text-text-secondary font-medium">Already added for this event</span>
                      )}
                    </div>
                    <div className="text-sm text-text-secondary">{preset.description}</div>
                    <div className="text-xs text-text-secondary mt-1">{preset.defaultItems.length} items included</div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="font-heading text-2xl text-brand-primary">₹{preset.price.toLocaleString('en-IN')}</div>
                    <Button
                      type="button"
                      onClick={() => startCustomize(preset)}
                      disabled={alreadyAdded}
                      className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                      data-testid={`customize-${preset.tier}`}
                    >
                      {alreadyAdded ? 'Added' : 'Customize & Add'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => { if (needsSubtype(selectedEvent.value)) { setStep('subtype'); setSelectedSubtype(null); } else { resetToEvent(); } }}
          className="text-text-secondary"
        >
          <CaretLeft size={16} className="mr-1" /> Back
        </Button>
      </div>
    );
  }

  // Step 4: Customize items + price
  if (step === 'customize' && editingTier) {
    const style = TIER_STYLES[editingTier.tier];
    const activeCount = packageItems.filter((i) => i.active).length;
    const categories = getAllItemCategories();

    return (
      <div className="space-y-4">
        <Breadcrumb />
        <Card className={`${style.border} border-2 ${style.bg}`}>
          <CardContent className="py-4">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${style.badge}`}>
                  {editingTier.name}
                </span>
                <span className="text-xs px-2 py-0.5 bg-text-primary text-sand-50 font-bold uppercase tracking-wider">
                  {selectedEvent.label}
                </span>
                {selectedSubtype && (
                  <span className="text-xs px-2 py-0.5 bg-brand-primary text-sand-50 font-bold uppercase tracking-wider">
                    {selectedSubtype.label}
                  </span>
                )}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setStep('tier'); setEditingTier(null); setPackageItems([]); }}>
                <X size={18} />
              </Button>
            </div>
            <div className="text-sm text-text-secondary mb-3">{editingTier.description}</div>
            <div className="flex items-center gap-3">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Package Price (₹)</span>
              <Input
                type="number"
                value={packagePrice}
                onChange={(e) => setPackagePrice(e.target.value)}
                className="rounded-none border-[#E8E1D5] w-48 font-heading text-xl"
                data-testid="package-price-input"
              />
            </div>
          </CardContent>
        </Card>

        {categories.map((cat) => {
          const catItems = packageItems.filter((i) => i.category === cat);
          return (
            <Card key={cat} className="bg-white border-[#E8E1D5]">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base font-heading">{cat}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {catItems.map((item) => {
                    const idx = packageItems.indexOf(item);
                    return (
                      <div
                        key={item.name}
                        className={`flex items-center gap-3 p-3 border rounded-md transition-all ${
                          item.active ? 'border-green-500 bg-green-50' : 'border-[#E8E1D5] bg-white'
                        }`}
                        data-testid={`pkg-item-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <Button
                          type="button"
                          onClick={() => toggleItem(idx)}
                          className={`rounded-full w-9 h-9 p-0 flex-shrink-0 ${
                            item.active ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
                          } text-white`}
                        >
                          {item.active ? <Check size={16} weight="bold" /> : <X size={16} weight="bold" />}
                        </Button>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{item.name}</div>
                          <div className="text-xs text-text-secondary">₹{item.defaultPrice.toLocaleString('en-IN')} each</div>
                        </div>
                        {item.category === 'Instruments' && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              type="button"
                              onClick={() => changeQty(idx, -1)}
                              variant="outline"
                              size="sm"
                              className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                              data-testid={`qty-minus-${item.name.toLowerCase().replace(/ /g, '-')}`}
                            >
                              <Minus size={14} />
                            </Button>
                            <span className="w-8 text-center font-bold text-sm" data-testid={`qty-val-${item.name.toLowerCase().replace(/ /g, '-')}`}>
                              {item.qty}
                            </span>
                            <Button
                              type="button"
                              onClick={() => changeQty(idx, 1)}
                              variant="outline"
                              size="sm"
                              className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                              data-testid={`qty-plus-${item.name.toLowerCase().replace(/ /g, '-')}`}
                            >
                              <Plus size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Work-images uploader — these images render in a swipeable
            lightbox on the customer side when they tap this photographer. */}
        <div className="mb-4 p-3 border-2 border-dashed border-[#E8E1D5] bg-white" data-testid="photo-work-images-block">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
              Work Photos for this Package ({workImages.length})
            </Label>
            <label className="cursor-pointer text-xs text-brand-primary hover:underline font-bold">
              <input type="file" accept="image/*" multiple
                onChange={handleWorkImageUpload}
                disabled={uploading}
                className="hidden"
                data-testid="photo-work-upload-input" />
              {uploading ? 'Uploading…' : '+ Upload images'}
            </label>
          </div>
          <p className="text-[11px] text-text-secondary mb-2">
            Customers see these as a swipeable gallery when they tap your card. Pick 3–10 of your best shots.
          </p>
          {workImages.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {workImages.map((url, i) => (
                <div key={i} className="relative group aspect-square bg-stone-100">
                  <img src={url} alt={`Work ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button"
                    onClick={() => setWorkImages(workImages.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`photo-work-remove-${i}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-4 bg-sand-50 p-4 border-t border-[#E8E1D5]">
          <Button
            type="button"
            onClick={handleSave}
            disabled={activeCount === 0}
            className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="save-package-btn"
          >
            Save {editingTier.name} Package ({activeCount} items)
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default PhotographerSelector;
